import { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import {
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FileDownload as FileDownloadIcon,
} from "@mui/icons-material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import styles from "@/components/general/imageViewer.module.css";
import { getImageUrl } from "@/helper/imageUrlHandler";
import {
  getCachedObjectUrl,
  getOrFetchObjectUrl,
  prefetchImage
} from "@/helper/imageCache";
import { useTranslation } from "react-i18next";

interface ImageViewerProps {
  open: boolean;
  onClose: () => void;
  images: Array<{
    id: string;
    name: string;
    view_link: string;
    download_link: string;
    extension: string;
  }>;
  initialImageIndex: number;
  childrenSubjectId?: string;
  doNotShowFileName?: boolean;
}

export default function ImageViewer({
  open,
  onClose,
  images,
  initialImageIndex,
  childrenSubjectId,
  doNotShowFileName,
}: ImageViewerProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [currentImageIndex, setCurrentImageIndex] = useState(initialImageIndex);
  const [imageLoading, setImageLoading] = useState(true);
  const [currentObjectUrl, setCurrentObjectUrl] = useState<string | null>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setCurrentImageIndex(initialImageIndex);
      setImageLoading(true);
      setCurrentObjectUrl(null);
    }
  }, [open, initialImageIndex]);

  const handlePrevious = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? images.length - 1 : prev - 1
    );
    setImageLoading(true);
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentImageIndex((prev) =>
      prev === images.length - 1 ? 0 : prev + 1
    );
    setImageLoading(true);
  }, [images.length]);

  const handleThumbnailClick = useCallback((index: number) => {
    setCurrentImageIndex(index);
    setImageLoading(true);
  }, []);

  const handleDownload = useCallback(() => {
    const currentImage = images[currentImageIndex];
    if (currentImage) {
      const downloadUrl = getImageUrl(currentImage.download_link, childrenSubjectId);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = currentImage.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [images, currentImageIndex, childrenSubjectId]);

  useEffect(() => {
    if (thumbnailRefs.current[currentImageIndex]) {
      thumbnailRefs.current[currentImageIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'center'
      });
    }
  }, [currentImageIndex]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!open) return;

    switch (event.key) {
      case "Escape":
        onClose();
        return;
      case "ArrowLeft":
        if (currentImageIndex > 0) {
          handlePrevious();
        }
        return;
      case "ArrowRight":
        if (currentImageIndex < images.length - 1) {
          handleNext();
        }
        return;
    }
  }, [
    open,
    onClose,
    handlePrevious,
    handleNext,
    currentImageIndex,
    images.length,
  ]);

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(event.targetTouches[0].clientX);
  }, []);

  const handleTouchMove = useCallback((event: React.TouchEvent) => {
    setTouchEnd(event.targetTouches[0].clientX);
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStart || !touchEnd) return;

    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentImageIndex < images.length - 1) {
      handleNext();
    } else if (isRightSwipe && currentImageIndex > 0) {
      handlePrevious();
    }

    setTouchStart(null);
    setTouchEnd(null);
  }, [
    touchStart,
    touchEnd,
    currentImageIndex,
    images.length,
    handleNext,
    handlePrevious,
  ]);

  useEffect(() => {
    if (open) {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const currentImage = images[currentImageIndex];
  const resolvedViewUrls = images.map((img) => getImageUrl(img.view_link, childrenSubjectId));

  useEffect(() => {
    if (!open || !currentImage) return;

    let isMounted = true;
    const url = resolvedViewUrls[currentImageIndex];
    const cached = getCachedObjectUrl(url);
    if (cached) {
      setCurrentObjectUrl(cached);
      setImageLoading(false);
    } else {
      setImageLoading(true);
      getOrFetchObjectUrl(url)
        .then((objectUrl) => {
          if (!isMounted) return;
          setCurrentObjectUrl(objectUrl);
          setImageLoading(false);
        })
        .catch(() => {
          if (!isMounted) return;
          setCurrentObjectUrl(null);
          setImageLoading(false);
        });
    }

    const prevIndex = currentImageIndex === 0 ? images.length - 1 : currentImageIndex - 1;
    const nextIndex = currentImageIndex === images.length - 1 ? 0 : currentImageIndex + 1;
    const neighborUrls = [resolvedViewUrls[prevIndex], resolvedViewUrls[nextIndex]];
    neighborUrls.forEach((neighborUrl) => {
      if (neighborUrl && !getCachedObjectUrl(neighborUrl)) {
        prefetchImage(neighborUrl).catch(() => undefined);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [
    open,
    currentImageIndex,
    currentImage,
    images.length,
    childrenSubjectId,
  ]);

  if (!currentImage) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: "rgba(0, 0, 0, 0.9)",
          borderRadius: isMobile ? 0 : "0.5rem",
          width: isMobile ? "100%" : "90vw",
          height: isMobile ? "100%" : "90vh",
        },
      }}
    >
      <DialogContent className={styles.imageViewerContent}>
        {/* Header with close button */}
        <Box className={styles.imageViewerHeader}>
          <Typography
            variant="h6"
            color="white"
            className={styles.imageViewerTitle}
            title={doNotShowFileName ? undefined : currentImage.name}
          >
            {doNotShowFileName ? "" : currentImage.name}
          </Typography>
          <Box className={styles.imageViewerHeaderButtons}>
            <Tooltip
              title={t("download")}
              placement="bottom"
              enterDelay={2000}
              enterNextDelay={2000}
            >
              <GenericButton
                variant="icon"
                onClick={handleDownload}
                className={styles.imageViewerDownloadButton}
              >
                <FileDownloadIcon />
              </GenericButton>
            </Tooltip>
            <Tooltip
              title={t("close")}
              placement="bottom"
              enterDelay={2000}
              enterNextDelay={2000}
            >
              <GenericButton
                variant="icon"
                onClick={onClose}
                className={styles.imageViewerCloseButton}
              >
                <CloseIcon />
              </GenericButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Main image container */}
        <Box className={styles.imageViewerMain}>
          {/* Navigation buttons */}
          {images.length > 1 && (
            <>
              {currentImageIndex > 0 && (
                <GenericButton
                  variant="icon"
                  onClick={handlePrevious}
                  className={`${styles.imageViewerNavButton} ${styles.imageViewerNavButtonLeft}`}
                >
                  <ChevronLeftIcon />
                </GenericButton>
              )}
              {currentImageIndex < images.length - 1 && (
                <GenericButton
                  variant="icon"
                  onClick={handleNext}
                  className={`${styles.imageViewerNavButton} ${styles.imageViewerNavButtonRight}`}
                >
                  <ChevronRightIcon />
                </GenericButton>
              )}
            </>
          )}

          {/* Main image */}
          <Box
            className={styles.imageViewerImageContainer}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {imageLoading && (
              <Box className={styles.imageViewerLoading}>
                <CircularProgress
                  size={60}
                  thickness={4}
                  sx={{ color: "white" }}
                />
              </Box>
            )}
            <img
              src={currentObjectUrl || undefined}
              alt={currentImage.name}
              className={styles.imageViewerImage}
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
              style={{ display: imageLoading ? "none" : "block" }}
            />
          </Box>
        </Box>

        {/* Thumbnail navigation */}
        {images.length > 1 && (
          <Box
            className={styles.imageViewerThumbnails}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            {images.map((image, index) => (
              <Box
                key={image.id}
                ref={(el) => (thumbnailRefs.current[index] = el as HTMLDivElement)}
                className={`${styles.imageViewerThumbnail} ${index === currentImageIndex ? styles.imageViewerThumbnailActive : ""
                  }`}
                onClick={() => handleThumbnailClick(index)}
              >
                <img
                  src={getCachedObjectUrl(getImageUrl(image.view_link, childrenSubjectId)) || undefined}
                  alt={`${image.name} thumbnail`}
                  className={styles.imageViewerThumbnailImage}
                />
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
